import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import styles from "./Salesrep.module.css";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DownloadIcon from "@mui/icons-material/Download";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import PercentOutlinedIcon from "@mui/icons-material/PercentOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/Clear";
import ToasterWithMessage from "../../components/shared-components/toaster";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { passwordRegexPattern, roles } from "../../helper";
import { globalContext } from "../../Provider/AppProvider";
import {
  ADD_SALES_REP,
  GET_SALES_REPS,
  GET_SALES_REPS_BY_EMAIL,
  GET_MANAGED_USERS_COUNT,
  GET_MANAGED_USERS_COUNT_BY_SALES_REP,
  GET_SALES_REP_USERS,
  GET_SALES_REP_BY_EMAIL,
  UPDATE_SALES_REP_BY_ID,
  CREATE_SALES_REP_COGNITO_USER,
  GET_MANAGED_USERS_BY_SALES_REP,
  UPDATE_SALES_REP_TOTAL_PAID_OUT,
  INCREMENT_SALES_REP_TOTAL_PAID_OUT,
} from "./api/SalesrepQuery";
import { GET_MANAGED_USERS } from "./api/SalesrepQuery";
import { toast } from "react-toastify";
interface SalesRepRecord {
  sr_id: string;
  sr_first_name: string;
  sr_last_name: string;
  sr_email: string;
  sr_phone: string;
  sr_commission_rate: number;
  sr_total_paid_out?: number | string | null;
  sr_created_at?: string;
  sr_updated_at?: string;
}

interface ManagedUser {
  u_id: string;
  u_full_name: string;
  u_email_id: string;
  u_sales_reps_id: string;
  tickets_sold_amount?: number | string | null;
  tickets?: Array<{
    soldTickets?: Array<{
      tp_quick_cut_amount?: number | string | null;
      tp_sales_rep_commission_override?: number | string | null;
    }>;
    listedCount?: {
      aggregate?: {
        count?: number | null;
      };
    };
  }>;
}

interface GetAllSalesRepsResponse {
  sales_reps: SalesRepRecord[];
}

interface ManagedUsersCountResponse {
  managedUsersCount?: {
    aggregate?: {
      count?: number;
    };
  };
}

interface RepViewModel {
  id: string;
  name: string;
  email: string;
  phone: string;
  commissionRate: number;
  commissionLabel: string;
  users: number;
  listedTickets: number;
  totalSales: number;
  lifetime: number;
  paid: number;
  currentBalance: number;
  avatar: string;
  color: string;
  createdAt?: string;
}

interface SalesRepUsersResponse {
  user: Array<{
    u_id: string;
    u_full_name: string;
    u_email_id: string;
    tickets_sold_amount?: number | string | null;
    tickets?: ManagedUser["tickets"];
  }>;
  user_aggregate?: {
    aggregate?: {
      count?: number;
    };
  };
}

interface AddRepForm {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  commissionRate: string;
  password: string;
  confirmPassword: string;
}

const SALESREP_PAYOUT_HISTORY_KEY = "salesrep-payout-history-v1";
const SALESREP_PAYOUT_DB_MIGRATED_KEY = "salesrep-payout-db-migrated-v1";

interface LegacyPayoutHistoryEntry {
  repId: string;
  amount: number;
}

const initialAddForm: AddRepForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  commissionRate: "10",
  password: "",
  confirmPassword: "",
};

const cardColors = ["#5d75f8", "#10b981", "#f59e0b", "#ef4444", "#06b6d4", "#8b5cf6"];

const toNumber = (value: unknown): number => {
  const numeric = Number(value ?? 0);
  return Number.isFinite(numeric) ? numeric : 0;
};

/** Sum resolved commission for each sold line; manual line amounts take precedence. */
const getTicketCommissionValue = (
  line: {
    tp_quick_cut_amount?: number | string | null;
    tp_sales_rep_commission_override?: number | string | null;
  },
  commissionRate: number,
): number => {
  const override = Number(line.tp_sales_rep_commission_override);
  if (
    line.tp_sales_rep_commission_override != null &&
    Number.isFinite(override) &&
    override >= 0
  ) {
    return override;
  }

  const quickCut = toNumber(line.tp_quick_cut_amount);
  if (quickCut <= 0 || commissionRate <= 0) {
    return 0;
  }

  return (quickCut * commissionRate) / 100;
};

const getUserCommissionTotal = (
  user: { tickets?: ManagedUser['tickets'] },
  commissionRate: number,
): number => {
  const total = (user.tickets || []).reduce((sum, ticket) => {
    const soldLines = ticket.soldTickets || [];
    return sum + soldLines.reduce((lineTotal, line) => {
      return lineTotal + getTicketCommissionValue(line, commissionRate);
    }, 0);
  }, 0);
  return Math.round(total * 100) / 100;
};

/** Listed placements for a user (Manage Tickets Listed tab: List + published). */
const getUserListedCount = (user: {
  tickets?: ManagedUser["tickets"];
}): number =>
  (user.tickets || []).reduce(
    (sum, ticket) => sum + toNumber(ticket.listedCount?.aggregate?.count),
    0,
  );

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);

const getInitials = (name: string) => {
  const segments = name
    .split(" ")
    .map((item) => item.trim())
    .filter(Boolean);
  if (!segments.length) return "SR";
  if (segments.length === 1) return segments[0].slice(0, 2).toUpperCase();
  return `${segments[0][0]}${segments[1][0]}`.toUpperCase();
};

export default function CommissionTracking() {
  const { error: showError, success, info } = ToasterWithMessage();
  const { globalState } = useContext<any>(globalContext);
  const userRole = globalState?.profile?.userRole || localStorage.getItem("x-hasura-user-role") || "";
  const activeHasuraRole = (localStorage.getItem("x-hasura-user-role") || userRole).toLowerCase().trim();
  const isMarketingUser = userRole === roles.MARKETING_USER;
  /** Portal super admin (Blake) — Hasura role `admin`, not seller `super_user`. */
  const isSuperAdmin = activeHasuraRole === roles.ADMIN;
  const canProcessPayout = isSuperAdmin || activeHasuraRole === roles.SUB_ADMIN;
  const loggedInUserId = (globalState?.profile?.id || "").toLowerCase().trim();
  const loggedInEmail = (globalState?.profile?.mailId || "").toLowerCase().trim();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('theme');
    if (saved) return saved === 'dark';
    return document.documentElement.getAttribute('data-theme') === 'dark';
  });
  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);
  const [openModal, setOpenModal] = useState(false);
  const [openUsersModal, setOpenUsersModal] = useState(false);
  const [openEditCommissionModal, setOpenEditCommissionModal] = useState(false);
  const [openPayoutModal, setOpenPayoutModal] = useState(false);
  const [openEditPaidOutModal, setOpenEditPaidOutModal] = useState(false);
  const [openPayoutHistoryModal, setOpenPayoutHistoryModal] = useState(false);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [selectedRep, setSelectedRep] = useState<RepViewModel | null>(null);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [paidOutInput, setPaidOutInput] = useState("");
  const [commissionRateInput, setCommissionRateInput] = useState("");
  const [searchText, setSearchText] = useState("");
  // Date range filter states
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [addForm, setAddForm] = useState<AddRepForm>(initialAddForm);
  const [submittingAddRep, setSubmittingAddRep] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSavingPaidOut, setIsSavingPaidOut] = useState(false);
  const [isProcessingPayout, setIsProcessingPayout] = useState(false);
  const payoutMigrationStartedRef = useRef(false);
  const [showDatePicker, setShowDatePicker] = useState(false);

  const { data: allSalesRepsData, loading: allSalesRepsLoading, refetch: refetchAllSalesReps } = useQuery<GetAllSalesRepsResponse>(GET_SALES_REPS, {
    skip: isMarketingUser,
    fetchPolicy: "network-only",
  });

  const { data: marketingSalesRepsData, loading: marketingSalesRepsLoading, refetch: refetchMarketingSalesReps } = useQuery<GetAllSalesRepsResponse>(GET_SALES_REPS_BY_EMAIL, {
    skip: !isMarketingUser || !loggedInEmail,
    variables: { email: loggedInEmail },
    fetchPolicy: "network-only",
  });

  const loggedInRep = useMemo(() => {
    if (!isMarketingUser) return null;
    const repsByIdentity = marketingSalesRepsData?.sales_reps || [];
    if (!repsByIdentity.length) return null;
    const exactEmailMatch = repsByIdentity.find(
      (rep) => (rep?.sr_email || "").toLowerCase().trim() === loggedInEmail
    );
    if (exactEmailMatch) return exactEmailMatch;
    if (loggedInUserId) {
      const userIdMatch = repsByIdentity.find(
        (rep) => (rep?.sr_id || "").toLowerCase().trim() === loggedInUserId
      );
      if (userIdMatch) return userIdMatch;
    }
    return repsByIdentity[0] || null;
  }, [isMarketingUser, marketingSalesRepsData, loggedInEmail, loggedInUserId]);

  const { data: allManagedUsersData, loading: allManagedUsersLoading, error: allManagedUsersError, refetch: refetchAllManagedUsers } = useQuery<{ managedUsers: ManagedUser[] }>(GET_MANAGED_USERS, {
    skip: isMarketingUser,
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error("[SalesReps] GetManagedUsers failed:", error);
    },
  });

  const { data: managedUsersByRepData, loading: managedUsersByRepLoading, error: managedUsersByRepError, refetch: refetchManagedUsersByRep } = useQuery<{ managedUsers: ManagedUser[] }>(GET_MANAGED_USERS_BY_SALES_REP, {
    skip: !isMarketingUser || !loggedInRep?.sr_id,
    variables: { salesRepId: loggedInRep?.sr_id },
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error("[SalesReps] GetManagedUsersBySalesRep failed:", error);
    },
  });

  const { data: allManagedUsersCountData, loading: allManagedUsersCountLoading, refetch: refetchAllManagedUsersCount } = useQuery<ManagedUsersCountResponse>(GET_MANAGED_USERS_COUNT, {
    skip: isMarketingUser,
    fetchPolicy: "network-only",
  });

  const { data: managedUsersCountByRepData, loading: managedUsersCountByRepLoading, refetch: refetchManagedUsersCountByRep } = useQuery<ManagedUsersCountResponse>(GET_MANAGED_USERS_COUNT_BY_SALES_REP, {
    skip: !isMarketingUser || !loggedInRep?.sr_id,
    variables: { salesRepId: loggedInRep?.sr_id },
    fetchPolicy: "network-only",
  });

  const data = isMarketingUser ? marketingSalesRepsData : allSalesRepsData;
  const loading = isMarketingUser ? marketingSalesRepsLoading : allSalesRepsLoading;
  const managedUsersData = isMarketingUser ? managedUsersByRepData : allManagedUsersData;
  const managedUsersLoading = isMarketingUser ? managedUsersByRepLoading : allManagedUsersLoading;
  const managedUsersCountData = isMarketingUser ? managedUsersCountByRepData : allManagedUsersCountData;
  const managedUsersCountLoading = isMarketingUser ? managedUsersCountByRepLoading : allManagedUsersCountLoading;
  const managedUsersError = isMarketingUser ? managedUsersByRepError : allManagedUsersError;

  useEffect(() => {
    if (!managedUsersError) return;
    showError("Failed to load sales rep user data. Refresh the page or contact support.");
  }, [managedUsersError, showError]);

  // Lazy query to get users for a specific sales rep
  const [fetchSalesRepUsers, { data: salesRepUsersData, loading: loadingSalesRepUsers }] =
    useLazyQuery<SalesRepUsersResponse>(GET_SALES_REP_USERS, {
      fetchPolicy: "network-only",
    });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [soldFilterMin, setSoldFilterMin] = useState("");
  const [soldFilterMax, setSoldFilterMax] = useState("");
  const [commissionFilterMin, setCommissionFilterMin] = useState("");
  const [commissionFilterMax, setCommissionFilterMax] = useState("");

  const [checkDuplicateEmail] = useLazyQuery(GET_SALES_REP_BY_EMAIL);
  const [addSalesRepMutation] = useMutation(ADD_SALES_REP);
  const [updateSalesRepMutation] = useMutation(UPDATE_SALES_REP_BY_ID);
  const [updateTotalPaidOutMutation] = useMutation(UPDATE_SALES_REP_TOTAL_PAID_OUT);
  const [incrementTotalPaidOutMutation] = useMutation(INCREMENT_SALES_REP_TOTAL_PAID_OUT);
  const [createCognitoUser] = useMutation(CREATE_SALES_REP_COGNITO_USER);

  // One-time: move browser payout totals into DB (only when DB is still 0).
  useEffect(() => {
    if (!isSuperAdmin || payoutMigrationStartedRef.current) return;
    if (!allSalesRepsData?.sales_reps?.length) return;
    if (localStorage.getItem(SALESREP_PAYOUT_DB_MIGRATED_KEY)) return;

    const savedHistory = localStorage.getItem(SALESREP_PAYOUT_HISTORY_KEY);
    if (!savedHistory) {
      localStorage.setItem(SALESREP_PAYOUT_DB_MIGRATED_KEY, "1");
      return;
    }

    let parsedHistory: LegacyPayoutHistoryEntry[] = [];
    try {
      const parsed = JSON.parse(savedHistory);
      if (!Array.isArray(parsed)) {
        localStorage.setItem(SALESREP_PAYOUT_DB_MIGRATED_KEY, "1");
        localStorage.removeItem(SALESREP_PAYOUT_HISTORY_KEY);
        return;
      }
      parsedHistory = parsed;
    } catch {
      localStorage.setItem(SALESREP_PAYOUT_DB_MIGRATED_KEY, "1");
      localStorage.removeItem(SALESREP_PAYOUT_HISTORY_KEY);
      return;
    }

    const localTotals = parsedHistory.reduce<Record<string, number>>((acc, entry) => {
      if (!entry?.repId) return acc;
      acc[entry.repId] = (acc[entry.repId] || 0) + toNumber(entry.amount);
      return acc;
    }, {});

    if (!Object.keys(localTotals).length) {
      localStorage.setItem(SALESREP_PAYOUT_DB_MIGRATED_KEY, "1");
      localStorage.removeItem(SALESREP_PAYOUT_HISTORY_KEY);
      return;
    }

    payoutMigrationStartedRef.current = true;

    const migratePayoutTotals = async () => {
      try {
        for (const rep of allSalesRepsData.sales_reps) {
          const localSum = localTotals[rep.sr_id] || 0;
          const dbTotal = toNumber(rep.sr_total_paid_out);
          if (localSum > 0 && dbTotal === 0) {
            await updateTotalPaidOutMutation({
              variables: { salesRepId: rep.sr_id, totalPaidOut: localSum },
            });
          }
        }
        localStorage.setItem(SALESREP_PAYOUT_DB_MIGRATED_KEY, "1");
        localStorage.removeItem(SALESREP_PAYOUT_HISTORY_KEY);
        await refetchAllSalesReps();
        info("Previous browser payout totals were saved to the database.");
      } catch (error) {
        console.error("[SalesReps] Payout migration failed:", error);
        payoutMigrationStartedRef.current = false;
      }
    };

    migratePayoutTotals();
  }, [isSuperAdmin, allSalesRepsData, updateTotalPaidOutMutation, refetchAllSalesReps, info]);

const filteredSalesRepUsers = useMemo(() => {
    const users = salesRepUsersData?.user || [];
    const minSold = soldFilterMin ? toNumber(soldFilterMin) : 0;
    const maxSold = soldFilterMax ? toNumber(soldFilterMax) : Infinity;
    const minCommission = commissionFilterMin ? toNumber(commissionFilterMin) : 0;
    const maxCommission = commissionFilterMax ? toNumber(commissionFilterMax) : Infinity;
    const commissionRate = selectedRep?.commissionRate ?? 0;

    return users.filter((user) => {
      const soldAmount = toNumber(user.tickets_sold_amount);
      const repCommission = getUserCommissionTotal(user, commissionRate);

      if (soldAmount < minSold) return false;
      if (maxSold !== Infinity && soldAmount > maxSold) return false;
      if (repCommission < minCommission) return false;
      if (maxCommission !== Infinity && repCommission > maxCommission) return false;
      return true;
    });
  }, [salesRepUsersData?.user, soldFilterMin, soldFilterMax, commissionFilterMin, commissionFilterMax, selectedRep]);

  const [errors, setErrors] = useState({
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  password: "",
  confirmPassword: "",
});

const validateForm = () => {
  const newErrors: any = {};

  // First Name
  if (!/^[A-Za-z\s]{2,50}$/.test(addForm.firstName.trim())) {
    newErrors.firstName = "Enter valid first name";
  }

  // Last Name
  if (!/^[A-Za-z\s]{2,50}$/.test(addForm.lastName.trim())) {
    newErrors.lastName = "Enter valid last name";
  }

  // Email
  if (
    !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(addForm.email)
  ) {
    newErrors.email = "Enter valid email address";
  }

  // Phone
  if (!/^[6-9]\d{9}$/.test(addForm.phone)) {
    newErrors.phone = "Enter valid 10-digit mobile number";
  }

  // Password
  if (
    !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/.test(
      addForm.password
    )
  ) {
    newErrors.password =
      "Password must contain uppercase, lowercase, number & special character";
  }

  // Confirm Password
  if (addForm.password !== addForm.confirmPassword) {
    newErrors.confirmPassword = "Passwords do not match";
  }

  setErrors(newErrors);

  return Object.keys(newErrors).length === 0;
};


  const reps = useMemo<RepViewModel[]>(() => {
    const repsData = [...(data?.sales_reps || [])].sort((a, b) => {
      const aTs = Date.parse(a.sr_updated_at || a.sr_created_at || "");
      const bTs = Date.parse(b.sr_updated_at || b.sr_created_at || "");
      return (Number.isFinite(bTs) ? bTs : 0) - (Number.isFinite(aTs) ? aTs : 0);
    });
    const managedUsers = managedUsersData?.managedUsers || [];
    
    return repsData.map((rep, idx) => {
      const usersForRep = managedUsers.filter((user) => user.u_sales_reps_id === rep.sr_id);
      
      const totalSales = usersForRep.reduce((sum, user) => sum + toNumber(user.tickets_sold_amount), 0);
      const commissionRate = toNumber(rep.sr_commission_rate ?? 0);
      const lifetime = usersForRep.reduce(
        (sum, user) => sum + getUserCommissionTotal(user, commissionRate),
        0,
      );
      const paidOut = toNumber(rep.sr_total_paid_out);
      const currentBalance = Math.max(0, lifetime - paidOut);
      
      const fullName = `${rep.sr_first_name || ""} ${rep.sr_last_name || ""}`.trim();

      return {
        id: rep.sr_id,
        name: fullName || rep.sr_email,
        email: rep.sr_email,
        phone: rep.sr_phone,
        commissionRate,
        commissionLabel: `${commissionRate}%`,
        users: usersForRep.length,
        listedTickets: usersForRep.reduce((sum, user) => sum + getUserListedCount(user), 0),
        totalSales,
        lifetime,
        paid: paidOut,
        currentBalance,
        avatar: getInitials(fullName || rep.sr_email),
        color: cardColors[idx % cardColors.length],
        createdAt: rep.sr_created_at,
      };
    });
  }, [data, managedUsersData, isMarketingUser, loggedInRep]);

  // Apply search and date range filter
  const filteredReps = useMemo(() => {
    let filtered = reps;

    // Apply search filter
    const search = searchText.trim().toLowerCase();
    if (search) {
      filtered = filtered.filter((rep) => {
        return (
          rep.name.toLowerCase().includes(search) ||
          rep.email.toLowerCase().includes(search) ||
          rep.phone?.toLowerCase().includes(search)
        );
      });
    }

    // Apply date range filter (based on rep createdAt)
    if (startDate || endDate) {
      filtered = filtered.filter((rep) => {
        if (!rep.createdAt) return false;
        const repDate = rep.createdAt.split('T')[0]; // YYYY-MM-DD
        if (startDate && repDate < startDate) return false;
        if (endDate && repDate > endDate) return false;
        return true;
      });
    }

    return filtered;
  }, [reps, searchText, startDate, endDate]);

  // Calculate statistics
  const totalReps = reps.length;
  const usersManaged = isMarketingUser
    ? reps.reduce((sum, rep) => sum + rep.users, 0)
    : (managedUsersCountData?.managedUsersCount?.aggregate?.count ?? managedUsersData?.managedUsers?.length ?? 0);
  const totalUserSales = reps.reduce((sum, rep) => sum + rep.totalSales, 0);
  const totalPendingPayouts = reps.reduce((sum, rep) => sum + rep.currentBalance, 0);
  
  const isStatsLoading = loading || managedUsersLoading || managedUsersCountLoading;
  const marketingUserRep = isMarketingUser ? reps[0] || null : null;

  // Fetch users for a specific rep
  const fetchRepUsers = (rep: RepViewModel) => {
    if (isMarketingUser && loggedInRep?.sr_id && rep.id !== loggedInRep.sr_id) {
      showError("You can only view your own commission data");
      return;
    }
    fetchSalesRepUsers({
      variables: {
        salesRepId: rep.id,
        limit: 100,
        offset: 0,
      },
    });
  };

  // Modal handlers
  const closeActionModals = () => {
    setOpenUsersModal(false);
    setOpenEditCommissionModal(false);
    setOpenPayoutModal(false);
    setOpenEditPaidOutModal(false);
    setOpenPayoutHistoryModal(false);
    setSelectedRep(null);
    setPayoutAmount("");
    setPaidOutInput("");
    setCommissionRateInput("");
  };

  const handleOpen = () => {
    if (isMarketingUser) {
      showError("You don't have permission to add sales reps");
      return;
    }
    setAddForm(initialAddForm);
    setOpenModal(true);
  };

  const handleClose = () => {
    setAddForm(initialAddForm);
    setShowPassword(false);
    setShowConfirmPassword(false);
    setOpenModal(false);
  };

  const handleRefreshData = async () => {
    setIsRefreshing(true);
    try {
      const refreshRequests: Promise<unknown>[] = [];
      refreshRequests.push(isMarketingUser ? refetchMarketingSalesReps() : refetchAllSalesReps());

      if (isMarketingUser) {
        if (loggedInRep?.sr_id) {
          refreshRequests.push(refetchManagedUsersByRep());
          refreshRequests.push(refetchManagedUsersCountByRep());
        }
      } else {
        refreshRequests.push(refetchAllManagedUsers());
        refreshRequests.push(refetchAllManagedUsersCount());
      }

      await Promise.all(refreshRequests);
      // success("Sales rep data refreshed successfully");
    } catch (error) {
      showError("Failed to refresh sales rep data");
      console.error("Refresh error:", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Date range handlers
  const handleStartDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setStartDate(e.target.value);
  };

  const handleEndDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEndDate(e.target.value);
  };

  const handleClearDateFilter = () => {
    setStartDate("");
    setEndDate("");
    setShowDatePicker(false);
  };

  const handleClearFilters = () => {
    setSearchText("");
    setStartDate("");
    setEndDate("");
    setShowDatePicker(false);
    success("Filters cleared");
  };

  const handleOpenUsersModal = (rep: RepViewModel) => {
    setSelectedRep(rep);
    setOpenUsersModal(true);
    setOpenMenuId(null);
    fetchRepUsers(rep);
  };

  const handleOpenEditCommissionModal = (rep: RepViewModel) => {
    setSelectedRep(rep);
    setCommissionRateInput(String(rep.commissionRate));
    setOpenEditCommissionModal(true);
    setOpenMenuId(null);
  };

  const handleOpenPayoutModal = (rep: RepViewModel) => {
    if (!canProcessPayout) {
      showError("You don't have permission to process payouts");
      return;
    }
    setSelectedRep(rep);
    setOpenPayoutModal(true);
    setOpenMenuId(null);
    setPayoutAmount("");
  };

  const handleOpenEditPaidOutModal = (rep: RepViewModel) => {
    if (!isSuperAdmin) {
      showError("Only Super Admin can edit total paid out");
      return;
    }
    setSelectedRep(rep);
    setPaidOutInput(rep.paid.toFixed(2));
    setOpenEditPaidOutModal(true);
    setOpenMenuId(null);
  };

  const handleOpenPayoutHistoryModal = (rep: RepViewModel) => {
    setSelectedRep(rep);
    setOpenPayoutHistoryModal(true);
    setOpenMenuId(null);
  };

  const handleProcessPayout = async () => {
    if (!canProcessPayout) {
      showError("You don't have permission to process payouts");
      return;
    }
    if (!selectedRep) {
      showError("No sales rep selected");
      return;
    }

    const amount = toNumber(payoutAmount);
    if (amount <= 0) {
      showError("Enter a valid payout amount");
      return;
    }
    if (amount > selectedRep.currentBalance) {
      showError(`Payout amount cannot exceed current balance of ${formatCurrency(selectedRep.currentBalance)}`);
      return;
    }

    setIsProcessingPayout(true);
    try {
      await incrementTotalPaidOutMutation({
        variables: { salesRepId: selectedRep.id, amount },
      });
      await (isMarketingUser ? refetchMarketingSalesReps() : refetchAllSalesReps());
      success(`Payout of ${formatCurrency(amount)} processed for ${selectedRep.name}`);
      closeActionModals();
    } catch (error) {
      console.error("[SalesReps] Process payout failed:", error);
      showError("Failed to process payout. Please try again.");
    } finally {
      setIsProcessingPayout(false);
    }
  };

  const handleSaveTotalPaidOut = async () => {
    if (!isSuperAdmin) {
      showError("Only Super Admin can edit total paid out");
      return;
    }
    if (!selectedRep) {
      showError("No sales rep selected");
      return;
    }

    const totalPaidOut = toNumber(paidOutInput);
    if (totalPaidOut < 0) {
      showError("Total paid out cannot be negative");
      return;
    }

    setIsSavingPaidOut(true);
    try {
      await updateTotalPaidOutMutation({
        variables: { salesRepId: selectedRep.id, totalPaidOut },
      });
      await (isMarketingUser ? refetchMarketingSalesReps() : refetchAllSalesReps());
      success(`Total paid out updated for ${selectedRep.name}`);
      closeActionModals();
    } catch (error) {
      console.error("[SalesReps] Update total paid out failed:", error);
      showError("Failed to update total paid out. Please try again.");
    } finally {
      setIsSavingPaidOut(false);
    }
  };

  const toggleMenu = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === id ? null : id);
  };

  const handleExportCsv = () => {
    if (!filteredReps.length) {
      showError("No sales reps to export");
      return;
    }

    const header = [
      "Name",
      "Email",
      "Phone",
      "Commission Rate",
      "Users Managed",
      "Total User Sales",
      "Lifetime Earned",
      "Total Paid Out",
      "Current Balance",
      "Created Date",
    ];

    const rows = filteredReps.map((rep) => [
      rep.name,
      rep.email,
      rep.phone,
      String(rep.commissionRate),
      String(rep.users),
      rep.totalSales.toFixed(2),
      rep.lifetime.toFixed(2),
      rep.paid.toFixed(2),
      rep.currentBalance.toFixed(2),
      rep.createdAt ? new Date(rep.createdAt).toLocaleDateString() : "N/A",
    ]);

    const csvData = [header, ...rows]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sales-reps-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    success("Sales reps exported successfully");
  };

  const handleAddFormChange = (field: keyof AddRepForm, value: string) => {
    setAddForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddSalesRep = async () => {
      if (!validateForm()) return;
    if (isMarketingUser) {
      showError("You don't have permission to add sales reps");
      return;
    }

    const firstName = addForm.firstName.trim();
    const lastName = addForm.lastName.trim();
    const email = addForm.email.trim().toLowerCase();
    const phone = addForm.phone.trim();
    const commissionRate = toNumber(addForm.commissionRate || 10);
    const password = addForm.password;
    const confirmPassword = addForm.confirmPassword;

    if (!firstName || !lastName || !email || !phone) {
      showError("Please enter all required fields");
      return;
    }

    if (!password) {
      showError("Password is required");
      return;
    }

    if (password.length < 8 || password.length > 30) {
      showError("Password must be between 8 and 30 characters");
      return;
    }

    if (!passwordRegexPattern.test(password)) {
      showError("Password must contain uppercase, lowercase, number and special character");
      return;
    }

    if (password !== confirmPassword) {
      showError("Passwords do not match");
      return;
    }

    if (commissionRate < 0 || commissionRate > 100) {
      showError("Commission rate must be between 0 and 100");
      return;
    }

    setSubmittingAddRep(true);
    try {
      // Check for duplicate email
      const { data: existingRepData } = await checkDuplicateEmail({
        variables: { email },
      });

      if (existingRepData?.sales_reps?.length) {
        showError("A sales rep with this email already exists");
        setSubmittingAddRep(false);
        return;
      }

      // Step 1: Create Cognito account — password stored ONLY in Cognito, never in DB
      try {
        await createCognitoUser({
          variables: {
            createMarketingUserInput: {
              adminEmailId: email,
              adminPassword: password,
              adminFirstName: firstName,
              adminLastName: lastName,
            },
          },
        });
      } catch (cognitoErr: any) {
        const cognitoMsg = (cognitoErr?.message || "").toLowerCase();
        if (!cognitoMsg.includes("usernameexistsexception") && !cognitoMsg.includes("user already exists")) {
          throw cognitoErr;
        }
      }

      // Step 2: Insert sales rep record in DB — no password field
      const response = await addSalesRepMutation({
        variables: {
          object: {
            sr_first_name: firstName,
            sr_last_name: lastName,
            sr_email: email,
            sr_phone: phone,
            sr_commission_rate: commissionRate,
          },
        },
      });

      if (!response.data?.insert_sales_reps_one?.sr_id) {
        showError("Sales rep creation failed");
        setSubmittingAddRep(false);
        return;
      }

      success("Sales rep added successfully");
      handleClose();
      setSearchText("");
      await handleRefreshData();
    } catch (err: any) {
      const errorMsg = err?.message || "Failed to add sales rep";
      showError(errorMsg);
      console.error("Add sales rep error:", err);
    } finally {
      setSubmittingAddRep(false);
    }
  };

  const handleEditCommission = async () => {
    if (!selectedRep) {
      showError("No sales rep selected");
      return;
    }

    const newRate = toNumber(commissionRateInput);
    if (newRate < 0 || newRate > 100) {
      showError("Commission rate must be between 0 and 100");
      return;
    }

    try {
      await updateSalesRepMutation({
        variables: {
          salesRepId: selectedRep.id,
          salesRepInput: { sr_commission_rate: newRate },
        },
      });
      success("Commission updated successfully");
      setOpenMenuId(null);
      closeActionModals();
      await (isMarketingUser ? refetchMarketingSalesReps() : refetchAllSalesReps());
    } catch (err: any) {
      showError(err?.message || "Failed to update commission");
    }
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, []);

  const formatDisplayDate = (dateString: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Helper to get filter button label
  const getDateFilterLabel = () => {
    if (startDate && endDate) {
      return `${formatDisplayDate(startDate)} - ${formatDisplayDate(endDate)}`;
    }
    if (startDate) {
      return `From: ${formatDisplayDate(startDate)}`;
    }
    if (endDate) {
      return `Until: ${formatDisplayDate(endDate)}`;
    }
    return "Filter by Date";
  };

  // Get payout balance for selected rep
  const payoutBalance = selectedRep?.currentBalance || 0;

  return (
    <div className={styles.container}>
      {/* HEADER */}
      <div className={styles.header}>
        <div>
          <p className={styles.subtitle}>Commission Tracking</p>
          <h1 className={styles.title}>Sales Reps</h1>
        </div>

        <div className={styles.headerRight}>
          <input
            className={styles.search}
            placeholder="Search by name/email/phone..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />

          <button 
            className={styles.iconBtn} 
            onClick={handleRefreshData}
            disabled={isRefreshing}
          >
            <RefreshRoundedIcon className={isRefreshing ? styles.spinning : ""} />
          </button>

          <button
            className={styles.iconBtn}
            onClick={() => setIsDarkMode(!isDarkMode)}
          >
            {isDarkMode ? <LightModeOutlinedIcon /> : <DarkModeOutlinedIcon />}
          </button>

          {/* Integrated Date Range Filter */}
          <div className={styles.dateFilterWrapper}>
            <button
              className={`${styles.dateFilterBtn} ${(startDate || endDate) ? styles.activeFilter : ""}`}
              onClick={() => setShowDatePicker(!showDatePicker)}
            >
              <CalendarTodayIcon className={styles.calendarIcon} />
              {(startDate || endDate) ? (
                <>
                  <span className={styles.selectedDate}>
                    {getDateFilterLabel()}
                  </span>
                  <ClearIcon 
                    className={styles.clearDateIcon}
                    onClick={handleClearDateFilter}
                  />
                </>
              ) : (
                <span>Filter by Date</span>
              )}
            </button>
            
            {showDatePicker && (
              <div className={styles.datePickerDropdown}>
                <div className={styles.dateRangeInputGroup}>
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={handleStartDateChange}
                    className={styles.dateInput}
                  />
                </div>
                <div className={styles.dateRangeInputGroup}>
                  <label>End Date</label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={handleEndDateChange}
                    className={styles.dateInput}
                  />
                </div>
                <div className={styles.datePickerActions}>
                  <button 
                    className={styles.datePickerClearBtn}
                    onClick={handleClearDateFilter}
                  >
                    Clear
                  </button>
                  <button 
                    className={styles.datePickerCloseBtn}
                    onClick={() => setShowDatePicker(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>

          {(searchText || startDate || endDate) && (
            <button className={styles.clearAllFiltersBtn} onClick={handleClearFilters}>
              Clear All
            </button>
          )}
        </div>
      </div>

      {/* STATS */}
      <div className={styles.statsRow}>
        <div className={styles.statCard}>
          <div>
            <p>Total Reps</p>
            <h2 style={{ color: "rgb(93 117 248)" }}>{loading ? "--" : totalReps}</h2>
          </div>
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="rgb(93 117 248)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 15H6a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="m14.305 16.53.923-.382"></path>
              <path d="m15.228 13.852-.923-.383"></path>
              <path d="m16.852 12.228-.383-.923"></path>
              <path d="m16.852 17.772-.383.924"></path>
              <path d="m19.148 12.228.383-.923"></path>
              <path d="m19.53 18.696-.382-.924"></path>
              <path d="m20.772 13.852.924-.383"></path>
              <path d="m20.772 16.148.924.383"></path>
              <circle cx="18" cy="15" r="3"></circle>
            </svg>
          </div>
        </div>

        <div className={styles.statCard}>
          <div>
            <p>Users Managed</p>
            <h2 style={{ color: "rgb(5 150 105)" }}>{managedUsersCountLoading ? "--" : usersManaged}</h2>
          </div>
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="rgb(5 150 105)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
              <path d="M16 3.128a4 4 0 0 1 0 7.744"></path>
              <path d="M22 21v-2a4 4 0 0 0-3-3.87"></path>
              <circle cx="9" cy="7" r="4"></circle>
            </svg>
          </div>
        </div>

        <div className={styles.statCard}>
          <div>
            <p>User Sales</p>
            <h2 style={{ color: "rgb(5 150 105)" }}>{isStatsLoading ? "--" : formatCurrency(totalUserSales)}</h2>
          </div>
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="rgb(5 150 105)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" x2="12" y1="2" y2="22"></line>
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
            </svg>
          </div>
        </div>

        <div className={styles.statCard}>
          <div>
            <p>Pending Payouts</p>
            <h2 style={{ color: "rgb(217 119 6)" }}>{isStatsLoading ? "--" : formatCurrency(totalPendingPayouts)}</h2>
          </div>
          <div>
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="rgb(217 119 6)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect width="20" height="14" x="2" y="5" rx="2"></rect>
              <line x1="2" x2="22" y1="10" y2="10"></line>
            </svg>
          </div>
        </div>
      </div>

      {/* EXPORT */}
      <div className={styles.exportRow}>
        <button className={styles.exportBtn} onClick={handleExportCsv}>
          <DownloadIcon />
          Export CSV
        </button>
        {/* {isMarketingUser && marketingUserRep && (
          <button className={styles.addBtn} onClick={() => handleOpenUsersModal(marketingUserRep)}>
            View Assigned Users
          </button>
        )} */}
        {!isMarketingUser && (
          <button className={styles.addBtn} onClick={handleOpen}>
            + Add Sales Rep
          </button>
        )}
      </div>

      {/* SALES REP CARDS */}
      <div className={styles.repGrid}>
        {loading && !data ? (
          <div className={`${styles.repCard} ${styles.repCardEmpty}`}>Loading sales reps...</div>
        ) : filteredReps.length === 0 ? (
          <div className={`${styles.repCard} ${styles.repCardEmpty}`}>
            No sales reps found
          </div>
        ) : (
          filteredReps.map((rep) => (
            <div key={rep.id} className={styles.repCard}>
              {/* HEADER */}
              <div className={styles.repHeader}>
                <div className={styles.avatar} style={{ backgroundColor: rep.color }}>
                  {rep.avatar}
                </div>
                <div>
                  <h3>{rep.name}</h3>
                  <p>{rep.email}</p>
                  <span className={styles.commission}>
                    {rep.commissionLabel} Commission
                  </span>
                </div>
                <div className={styles.menuContainer}>
                  <MoreVertIcon
                    className={styles.moreIcon}
                    onClick={(e) => toggleMenu(rep.id, e)}
                  />
                  {openMenuId === rep.id && (
                    <div className={styles.dropdownMenu}>
                      <button className={styles.menuItem} onClick={() => handleOpenUsersModal(rep)}>
                        <GroupOutlinedIcon className={styles.menuItemIcon} />
                        View Users
                      </button>
                      {!isMarketingUser && (
                        <>
                          {canProcessPayout && (
                            <button
                              className={styles.menuItem}
                              onClick={() => handleOpenPayoutModal(rep)}
                            >
                              <PaidOutlinedIcon className={styles.menuItemIcon} />
                              Process Payout
                            </button>
                          )}
                          {isSuperAdmin && (
                            <button
                              className={styles.menuItem}
                              onClick={() => handleOpenEditPaidOutModal(rep)}
                            >
                              <PaidOutlinedIcon className={styles.menuItemIcon} />
                              Edit Total Paid Out
                            </button>
                          )}
                          <button className={styles.menuItem} onClick={() => handleOpenEditCommissionModal(rep)}>
                            <PercentOutlinedIcon className={styles.menuItemIcon} />
                            Edit Commission
                          </button>
                          <button className={styles.menuItem} onClick={() => handleOpenPayoutHistoryModal(rep)}>
                            <HistoryOutlinedIcon className={styles.menuItemIcon} />
                            Payout Summary
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* USERS */}
              <div className={styles.repStats}>
                <div className={styles.users}>
                  <h2>{rep.users}</h2>
                  <p># Users</p>
                </div>
                <div className={styles.listed}>
                  <h2>{rep.listedTickets}</h2>
                  <p>Listed Tickets</p>
                </div>
              </div>

              {/* FINANCIAL */}
              <div className={styles.financial}>
                <div>
                  <span>Total User Sales:</span>
                  <span>{formatCurrency(rep.totalSales)}</span>
                </div>
                <div>
                  <span>Lifetime Earned:</span>
                  <span className={styles.green}>{formatCurrency(rep.lifetime)}</span>
                </div>
                <div>
                  <span>Total Paid Out:</span>
                  <span>{formatCurrency(rep.paid)}</span>
                </div>
                <div>
                  <span>Current Balance:</span>
                  <span className={styles.blue}>{formatCurrency(rep.currentBalance)}</span>
                </div>
              </div>

              {canProcessPayout && (
                <div className={styles.repCardActions}>
                  <button
                    className={styles.payoutBtn}
                    onClick={() => handleOpenPayoutModal(rep)}
                  >
                    Process Payout
                  </button>
                  {isSuperAdmin && (
                    <button
                      className={styles.editPaidOutBtn}
                      onClick={() => handleOpenEditPaidOutModal(rep)}
                    >
                      Edit Total Paid Out
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* <div className={styles.footer}>
        Made with <span>Emergent</span>
      </div> */}

      {/* Add Sales Rep Modal */}
{openModal && (
  <div className={styles.modalOverlay}>
    <div className={styles.modalBox}>
      <div className={styles.modalHeader}>
        <h3>Add Sales Rep</h3>
        <button className={styles.closeBtn} onClick={handleClose}>
          ✕
        </button>
      </div>

      <div className={styles.modalForm}>
        <div className={styles.formRow}>
          <div className={styles.inputGroup}>
            <label>First Name</label>

            <input
              type="text"
              value={addForm.firstName}
              maxLength={50}
              placeholder="Enter first name"
              onChange={(e) =>
                handleAddFormChange(
                  "firstName",
                  e.target.value.replace(/[^A-Za-z\s]/g, "")
                )
              }
            />

            {errors.firstName && (
              <span className={styles.errorText}>
                {errors.firstName}
              </span>
            )}
          </div>

          <div className={styles.inputGroup}>
            <label>Last Name</label>

            <input
              type="text"
              value={addForm.lastName}
              maxLength={50}
              placeholder="Enter last name"
              onChange={(e) =>
                handleAddFormChange(
                  "lastName",
                  e.target.value.replace(/[^A-Za-z\s]/g, "")
                )
              }
            />

            {errors.lastName && (
              <span className={styles.errorText}>
                {errors.lastName}
              </span>
            )}
          </div>
        </div>

        <div className={styles.formRow}>
          <div className={styles.inputGroup}>
            <label>Email</label>

            <input
              type="email"
              value={addForm.email}
              placeholder="Enter email"
              onChange={(e) =>
                handleAddFormChange("email", e.target.value)
              }
            />

            {errors.email && (
              <span className={styles.errorText}>
                {errors.email}
              </span>
            )}
          </div>

          <div className={styles.inputGroup}>
            <label>Phone</label>

            <input
              type="text"
              value={addForm.phone}
              maxLength={10}
              placeholder="Enter 10-digit mobile number"
              onChange={(e) =>
                handleAddFormChange(
                  "phone",
                  e.target.value.replace(/\D/g, "")
                )
              }
            />

            {errors.phone && (
              <span className={styles.errorText}>
                {errors.phone}
              </span>
            )}
          </div>
        </div>

        <div className={styles.inputGroupFull}>
          <label>Commission Rate (%)</label>

          <input
            type="number"
            min="0"
            max="100"
            step="1"
            value={addForm.commissionRate}
            onChange={(e) =>
              handleAddFormChange(
                "commissionRate",
                e.target.value
              )
            }
          />
        </div>

        <div className={styles.formRow}>
          <div className={styles.inputGroup}>
            <label>Password</label>

            <div className={styles.passwordInputWrapper}>
              <input
                type={showPassword ? "text" : "password"}
                value={addForm.password}
                placeholder="Min 8 chars, upper, lower, number, symbol"
                onChange={(e) =>
                  handleAddFormChange(
                    "password",
                    e.target.value
                  )
                }
              />

              <button
                type="button"
                className={styles.passwordToggleBtn}
                onClick={() => setShowPassword((p) => !p)}
                tabIndex={-1}
              >
                {showPassword ? (
                  <VisibilityOffOutlinedIcon fontSize="small" />
                ) : (
                  <VisibilityOutlinedIcon fontSize="small" />
                )}
              </button>
            </div>

            {errors.password && (
              <span className={styles.errorText}>
                {errors.password}
              </span>
            )}
          </div>

          <div className={styles.inputGroup}>
            <label>Confirm Password</label>

            <div className={styles.passwordInputWrapper}>
              <input
                type={
                  showConfirmPassword ? "text" : "password"
                }
                value={addForm.confirmPassword}
                placeholder="Re-enter password"
                onChange={(e) =>
                  handleAddFormChange(
                    "confirmPassword",
                    e.target.value
                  )
                }
              />

              <button
                type="button"
                className={styles.passwordToggleBtn}
                onClick={() =>
                  setShowConfirmPassword((p) => !p)
                }
                tabIndex={-1}
              >
                {showConfirmPassword ? (
                  <VisibilityOffOutlinedIcon fontSize="small" />
                ) : (
                  <VisibilityOutlinedIcon fontSize="small" />
                )}
              </button>
            </div>

            {errors.confirmPassword && (
              <span className={styles.errorText}>
                {errors.confirmPassword}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.modalFooter}>
        <button className={styles.cancelBtn} onClick={handleClose}>
          Cancel
        </button>

        <button
          className={styles.addRepBtn}
          onClick={handleAddSalesRep}
          disabled={submittingAddRep}
        >
          {submittingAddRep ? "Adding..." : "Add Rep"}
        </button>
      </div>
    </div>
  </div>
)}

      {/* View Users Modal */}
      {openUsersModal && selectedRep && (
        <div className={styles.modalOverlay}>
          <div className={styles.actionModalBox}>
            <div className={styles.actionModalHeader}>
              <h3>{selectedRep.name}'s Users ({loadingSalesRepUsers ? "..." : (salesRepUsersData?.user?.length || 0)})</h3>
              <button className={styles.circleCloseBtn} onClick={closeActionModals}>
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>

                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px', alignItems: 'flex-end' }}>
                <div style={{ minWidth: 160, display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 12, marginBottom: 4 }}>Sold Amount Min</label>
                  <input
                    type="number"
                    min="0"
                    value={soldFilterMin}
                    onChange={(e) => setSoldFilterMin(e.target.value)}
                    placeholder="0"
                    style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc' }}
                  />
                </div>
                <div style={{ minWidth: 160, display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 12, marginBottom: 4 }}>Sold Amount Max</label>
                  <input
                    type="number"
                    min="0"
                    value={soldFilterMax}
                    onChange={(e) => setSoldFilterMax(e.target.value)}
                    placeholder="Any"
                    style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc' }}
                  />
                </div>
                <div style={{ minWidth: 160, display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 12, marginBottom: 4 }}>Rep Commission Min</label>
                  <input
                    type="number"
                    min="0"
                    value={commissionFilterMin}
                    onChange={(e) => setCommissionFilterMin(e.target.value)}
                    placeholder="0"
                    style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc' }}
                  />
                </div>
                <div style={{ minWidth: 160, display: 'flex', flexDirection: 'column' }}>
                  <label style={{ fontSize: 12, marginBottom: 4 }}>Rep Commission Max</label>
                  <input
                    type="number"
                    min="0"
                    value={commissionFilterMax}
                    onChange={(e) => setCommissionFilterMax(e.target.value)}
                    placeholder="Any"
                    style={{ padding: '8px 10px', borderRadius: 8, border: '1px solid #ccc' }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSoldFilterMin("");
                    setSoldFilterMax("");
                    setCommissionFilterMin("");
                    setCommissionFilterMax("");
                  }}
                  style={{
                    background: '#f3f4f6',
                    border: '1px solid #d1d5db',
                    borderRadius: 8,
                    padding: '10px 14px',
                    cursor: 'pointer',
                    height: 42,
                    minWidth: 100,
                  }}
                >
                  Reset Filters
                </button>
              </div>
              <div className={styles.usersTableWrap}>
                <div className={styles.usersTableHead}>
                  <span>NAME</span>
                  <span>EMAIL</span>
                  <span>SOLD</span>
                  <span>REP COMMISSION</span>
                </div>

              {loadingSalesRepUsers ? (
                <p className={styles.emptyUsers}>Loading users...</p>
              ) : filteredSalesRepUsers.length > 0 ? (
                filteredSalesRepUsers.map((user) => (
                  <div key={user.u_id} className={styles.usersTableRow}>
                    <span>{user.u_full_name}</span>
                    <span>{user.u_email_id}</span>
                    <span>{formatCurrency(toNumber(user.tickets_sold_amount))}</span>
                    <span>
                      {formatCurrency(
                        getUserCommissionTotal(user, selectedRep?.commissionRate ?? 0),
                      )}
                    </span>
                  </div>
                ))
              ) : (
                <p className={styles.emptyUsers}>No users found for this sales rep</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Edit Commission Modal */}
      {openEditCommissionModal && selectedRep && (
        <div className={styles.modalOverlay}>
          <div className={styles.actionModalBox}>
            <div className={styles.actionModalHeader}>
              <h3>Edit Commission Rate</h3>
              <button className={styles.circleCloseBtn} onClick={closeActionModals}>
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>

            <div className={styles.repInfoCard}>
              <p>Sales Rep</p>
              <h4>{selectedRep.name}</h4>
              <span>Current Rate: {selectedRep.commissionLabel}</span>
            </div>

            <div className={styles.payoutInputGroup}>
              <label>New Commission Rate (%)</label>
              <input
                type="number"
                min={0}
                max={100}
                value={commissionRateInput}
                onChange={(e) => setCommissionRateInput(e.target.value)}
                className={styles.payoutInput}
              />
            </div>

            <div className={styles.payoutModalFooter}>
              <button className={styles.cancelBtn} onClick={closeActionModals}>
                Cancel
              </button>
              <button
                className={styles.processBtn}
                onClick={handleEditCommission}
                disabled={!commissionRateInput}
              >
                Update Rate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Total Paid Out Modal (Super Admin only) */}
      {openEditPaidOutModal && selectedRep && (
        <div className={styles.modalOverlay}>
          <div className={styles.actionModalBox}>
            <div className={styles.payoutModalHeader}>
              <h3>Edit Total Paid Out for {selectedRep.name}</h3>
              <button className={styles.circleCloseBtn} onClick={closeActionModals}>
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>

            <div className={styles.payoutModalContent}>
              <div className={styles.balanceRow}>
                <span>Lifetime Earned</span>
                <span className={styles.balanceAmount}>{formatCurrency(selectedRep.lifetime)}</span>
              </div>
              <div className={styles.balanceRow}>
                <span>Current Balance (after save)</span>
                <span className={styles.balanceAmount}>
                  {formatCurrency(Math.max(0, selectedRep.lifetime - toNumber(paidOutInput)))}
                </span>
              </div>

              <div className={styles.payoutInputGroup}>
                <label>Total Paid Out ($)</label>
                <input
                  type="number"
                  placeholder="Enter cumulative amount paid"
                  min="0"
                  step="0.01"
                  value={paidOutInput}
                  onChange={(e) => setPaidOutInput(e.target.value)}
                  className={styles.payoutInput}
                />
                <p className={styles.paidOutHint}>
                  Set the full running total — use this to enter historical commissions already paid.
                </p>
              </div>
            </div>

            <div className={styles.payoutModalFooter}>
              <button className={styles.cancelBtn} onClick={closeActionModals}>
                Cancel
              </button>
              <button
                className={styles.processBtn}
                onClick={handleSaveTotalPaidOut}
                disabled={isSavingPaidOut || paidOutInput === "" || toNumber(paidOutInput) < 0}
              >
                {isSavingPaidOut ? "Saving..." : "Save Total"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Process Payout Modal */}
      {openPayoutModal && selectedRep && (
        <div className={styles.modalOverlay}>
          <div className={styles.actionModalBox}>
            <div className={styles.payoutModalHeader}>
              <h3>Process Payout for {selectedRep.name}</h3>
              <button className={styles.circleCloseBtn} onClick={closeActionModals}>
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>

            <div className={styles.payoutModalContent}>
              <div className={styles.balanceRow}>
                <span>Current Balance</span>
                <span className={styles.balanceAmount}>{formatCurrency(payoutBalance)}</span>
              </div>

              <div className={styles.payoutInputGroup}>
                <label>Payout Amount ($)</label>
                <input
                  type="number"
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                  value={payoutAmount}
                  onChange={(e) => setPayoutAmount(e.target.value)}
                  className={styles.payoutInput}
                />
              </div>
            </div>

            <div className={styles.payoutModalFooter}>
              <button className={styles.cancelBtn} onClick={closeActionModals}>
                Cancel
              </button>
              <button
                className={styles.processBtn}
                onClick={handleProcessPayout}
                disabled={isProcessingPayout || !payoutAmount || toNumber(payoutAmount) <= 0}
              >
                {isProcessingPayout ? "Processing..." : "Process Payout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payout Summary Modal */}
      {openPayoutHistoryModal && selectedRep && (
        <div className={styles.modalOverlay}>
          <div className={styles.historyModalBox}>
            <div className={styles.actionModalHeader}>
              <h3>{selectedRep.name}'s Payout Summary</h3>
              <button className={styles.circleCloseBtn} onClick={closeActionModals}>
                <CloseRoundedIcon fontSize="small" />
              </button>
            </div>

            <div className={styles.historySummary}>
              <div className={`${styles.historySummaryCard} ${styles.summaryGreen}`}>
                <span>Lifetime Earned</span>
                <strong>{formatCurrency(selectedRep.lifetime)}</strong>
              </div>
              <div className={`${styles.historySummaryCard} ${styles.summaryGray}`}>
                <span>Total Paid Out</span>
                <strong>{formatCurrency(selectedRep.paid)}</strong>
              </div>
              <div className={`${styles.historySummaryCard} ${styles.summaryAmber}`}>
                <span>Current Balance</span>
                <strong>{formatCurrency(payoutBalance)}</strong>
              </div>
            </div>

            <p className={styles.emptyUsers}>
              {isSuperAdmin
                ? "Use Edit Total Paid Out to set historical commissions, or Process Payout to record a new payment."
                : canProcessPayout
                  ? "Use Process Payout to record a new payment. Contact Super Admin to adjust historical totals."
                  : "Total paid out is maintained by Super Admin."}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}